import { useState, useEffect } from 'react';
import { secureLocalStorageGet, secureLocalStorageSet } from '@/lib/security';

interface SessionMemoryData {
  position: number;
  timestamp: number;
  videoId: string;
}

const SESSION_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

export function useSessionMemory(videoId: string) {
  const [hasSessionData, setHasSessionData] = useState(false);
  const [sessionPosition, setSessionPosition] = useState<number | null>(null);

  // Check for existing session data on mount
  useEffect(() => {
    if (!videoId) return;

    const sessionKey = `video-session-${videoId}`;
    const sessionData = secureLocalStorageGet(sessionKey);

    if (sessionData && typeof sessionData === 'object') {
      try {
        // Validate session data structure
        if (
          typeof sessionData.position === 'number' &&
          typeof sessionData.timestamp === 'number' &&
          typeof sessionData.videoId === 'string' &&
          sessionData.videoId === videoId
        ) {
          const now = Date.now();
          
          // Check if session is still valid (within 24 hours)
          if (now - sessionData.timestamp < SESSION_DURATION && sessionData.position > 5) {
            setHasSessionData(true);
            setSessionPosition(sessionData.position);
          } else {
            // Clean up expired session data
            localStorage.removeItem(sessionKey);
          }
        } else {
          // Invalid data structure, clean up
          localStorage.removeItem(sessionKey);
        }
      } catch (error) {
        console.error('Error validating session data:', error);
        localStorage.removeItem(sessionKey);
      }
    }
  }, [videoId]);

  const saveSessionPosition = (position: number) => {
    if (!videoId || position <= 5 || typeof position !== 'number') return;

    const sessionData: SessionMemoryData = {
      position,
      timestamp: Date.now(),
      videoId
    };

    secureLocalStorageSet(`video-session-${videoId}`, sessionData);
  };

  const clearSessionData = () => {
    if (!videoId) return;
    localStorage.removeItem(`video-session-${videoId}`);
    setHasSessionData(false);
    setSessionPosition(null);
  };

  const consumeSessionData = () => {
    setHasSessionData(false);
  };

  return {
    hasSessionData,
    sessionPosition,
    saveSessionPosition,
    clearSessionData,
    consumeSessionData
  };
}